// client/src/services/lexistarAssurance.ts  (converted from .js, ST-00193; CLAUDE.md TS rule)
//
// LexiStar assurance API client (ST-00188, M3-U1; contracts
// docs/lexistar/contracts/m3-contracts.md §7.1, §7.3 re-plan, §2.4 realtime).
// Server: server/modules/lexistar/assurance (M3-A1) and the trips module's
// re-plan (M3-A3), mounted at /api/lexistar.
//
// Thin wrapper over the shared axios instance in `services/api` (cookie-session
// auth, CSRF header, single-flight 401 refresh; `api` already carries `/api`).
// Every helper resolves to the response BODY. Payloads use the 026 column names
// (snake_case). Action routes use the contract's `:verb` suffix literally
// (POST /review-items/:id:accept, POST /recompute-jobs/:id:signOff, …).
//
// The contract names the routes and the content of each response but not every
// envelope key, so the pages read bodies through the tolerant pickers below
// (`pickList`, `pickObject`) — e.g. a list may come back as `{ review_items }`,
// `{ items }` or `{ rows }`. What the UI expects is written next to each helper.
//
// Realtime (§2.4): `lexistar:review` → room tenant:{id}:review (review-queue
// counters), `lexistar:recompute` → tenant:{id} (job state). Joined on the
// shared /tracking namespace; see subscribeAssurance().

import { io } from 'socket.io-client';
import type { AxiosResponse } from 'axios';
import api from './api';

const LX = '/lexistar';

/**
 * A response body. The contract names the content, not every envelope key, so the pages read
 * bodies through the tolerant pickers below; the helpers hand the body back unchanged.
 */
export type AssuranceBody = unknown;
type Obj = Record<string, unknown>;
/** Query or body fields as the pages pass them (lists become comma lists in a query). */
export type AssuranceParams = Record<string, unknown>;

/** Drop undefined / null / '' so the API never sees `state=`. */
const clean = (params: AssuranceParams = {}): Obj =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
const body = (res: AxiosResponse<unknown>): AssuranceBody => res.data;
/** Arrays become comma lists. */
const query = (params: AssuranceParams = {}): Obj =>
  clean(
    Object.fromEntries(
      Object.entries(params).map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v])
    )
  );

const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * First array found under `keys` (or the body itself when it is an array).
 * Tolerates `{ rows }`, `{ data }`, `{ items }` and bare-array bodies.
 */
export const pickList = (data: unknown, ...keys: string[]): unknown[] => {
  if (Array.isArray(data)) return data;
  if (!isObj(data)) return [];
  for (const k of [...keys, 'data', 'rows', 'items']) {
    const v = data[k];
    if (Array.isArray(v)) return v;
  }
  return [];
};

/** First plain object under `keys`, else the body itself (single-resource envelopes). */
export const pickObject = (data: unknown, ...keys: string[]): Obj | null => {
  if (!isObj(data)) return null;
  for (const k of keys) {
    const v = data[k];
    if (isObj(v)) return v;
  }
  return data;
};

type Id = string | number;

// ── Review queue (T4-S11; §7.1) ──────────────────────────────────────────────

/**
 * GET /review-items — filters: state, kind, cause_key, trip_id, asset_key, from, to
 * (lists become comma lists). Body: rows (`review_items` | `items` | `rows`) plus the
 * header stats `{ open, oldest_at, share_this_week }` (under `stats`, or top level).
 */
export const listReviewItems = (params?: AssuranceParams): Promise<AssuranceBody> =>
  api.get(`${LX}/review-items`, { params: query(params) }).then(body);

/**
 * GET /review-items/:id — the item plus its evidence: ± 5 min `positions`
 * (telemetry.positions_scoped), the `subject` row, `factors` (ConfidenceFactorV1[]),
 * `related_events`, `corrections`. Read with readReviewDetail().
 */
export const getReviewItem = (id: Id): Promise<AssuranceBody> => api.get(`${LX}/review-items/${id}`).then(body);

/** Accept as recorded; the reason is optional. */
export const acceptReviewItem = (id: Id, { reason }: { reason?: string | null } = {}): Promise<AssuranceBody> =>
  api.post(`${LX}/review-items/${id}:accept`, clean({ reason })).then(body);

/** Dismiss (not a real record); a reason is required. */
export const dismissReviewItem = (id: Id, { reason }: { reason: string }): Promise<AssuranceBody> =>
  api.post(`${LX}/review-items/${id}:dismiss`, { reason }).then(body);

/**
 * Correct: { kind: 'split'|'merge'|'void'|'reclassify'|'reassign', after, reason }.
 * The server computes the correction's anchor from the subject and resolves the item.
 */
export const correctReviewItem = (
  id: Id,
  { kind, after, reason }: { kind: string; after?: unknown; reason?: string | null }
): Promise<AssuranceBody> =>
  api.post(`${LX}/review-items/${id}:correct`, { kind, after, reason }).then(body);

/**
 * Bulk triage: { cause_key } or { ids }, outcome 'accept' | 'dismiss', reason.
 * Body: the number resolved (`resolved` | `count` | `updated`).
 */
export const bulkResolveReviewItems = ({
  cause_key,
  ids,
  outcome,
  reason,
}: {
  cause_key?: string | null;
  ids?: Id[] | null;
  outcome: string;
  reason?: string | null;
}): Promise<AssuranceBody> =>
  api
    .post(
      `${LX}/review-items:bulk`,
      clean({ cause_key, ids: ids?.length ? ids : undefined, outcome, reason })
    )
    .then(body);

// ── Trip record: sign-off, amendments, audit (T4-S13; §7.1) and re-plan (§7.3) ─

/** POST /trips/:id:signOff — refused (409) while review items are open or the trip is not complete. */
export const signOffTrip = (id: Id): Promise<AssuranceBody> => api.post(`${LX}/trips/${id}:signOff`, {}).then(body);

/**
 * Request an amendment of a signed-off trip: { reason, changes } where changes may hold
 * `load`, `driver_id`, `dispatch_net_kg`, `receipt_net_kg`, `status_flags`, correction ids.
 */
export const requestTripAmendment = (
  id: Id,
  { reason, changes }: { reason: string; changes: unknown }
): Promise<AssuranceBody> =>
  api.post(`${LX}/trips/${id}:amend`, { reason, changes }).then(body);

/** Second user only (the database rejects approved_by = requested_by). */
export const approveTripAmendment = (id: Id): Promise<AssuranceBody> =>
  api.post(`${LX}/trip-amendments/${id}:approve`, {}).then(body);

export const rejectTripAmendment = (id: Id, { reason }: { reason?: string | null } = {}): Promise<AssuranceBody> =>
  api.post(`${LX}/trip-amendments/${id}:reject`, clean({ reason })).then(body);

/**
 * The trip's amendment requests. NOT in the §7.1 route list (contract gap reported by
 * M3-U1): the page first reads `amendments` from GET /trips/:id, then tries this route
 * and treats a 404 as "none".
 */
export const listTripAmendments = (tripId: Id): Promise<AssuranceBody> =>
  api.get(`${LX}/trip-amendments`, { params: { trip_id: tripId } }).then(body);

/** GET /trips/:id/audit — audit_ledger rows (hash chain) for the trip. */
export const getTripAudit = (id: Id): Promise<AssuranceBody> => api.get(`${LX}/trips/${id}/audit`).then(body);

/**
 * Mid-trip re-plan (T4-S18, M3-A3): { to_location_id? | route_id?, via_location_id?,
 * reason_code, reason } → new leg set, budget_adjusted_l, plan_version + 1.
 */
export const replanTrip = (id: Id, data: AssuranceParams): Promise<AssuranceBody> =>
  api.post(`${LX}/trips/${id}:replan`, clean(data)).then(body);

// ── Declarations (T4-S14; §7.1) ──────────────────────────────────────────────

/** { declarations } — filters: state. */
export const listDeclarations = (params?: AssuranceParams): Promise<AssuranceBody> =>
  api.get(`${LX}/declarations`, { params: query(params) }).then(body);

export const getDeclaration = (id: Id): Promise<AssuranceBody> => api.get(`${LX}/declarations/${id}`).then(body);

/**
 * Create a draft: { scope: { kind, ids, geometry?, corridor_m? }, valid_from, valid_to,
 * effect, reason_code?, reason, alt_corridor?, template? }.
 */
export const createDeclaration = (data: AssuranceParams): Promise<AssuranceBody> => api.post(`${LX}/declarations`, clean(data)).then(body);

/** Counts in scope: { preview: { trips, deviations, delay_hours, computed_at } } (or top level). */
export const previewDeclaration = (id: Id): Promise<AssuranceBody> =>
  api.post(`${LX}/declarations/${id}:preview`, {}).then(body);

/** Future → advisory going forward; past or spanning → a recompute job (recompute_job_id). */
export const publishDeclaration = (id: Id): Promise<AssuranceBody> =>
  api.post(`${LX}/declarations/${id}:publish`, {}).then(body);

export const retractDeclaration = (id: Id, { reason }: { reason?: string | null } = {}): Promise<AssuranceBody> =>
  api.post(`${LX}/declarations/${id}:retract`, clean({ reason })).then(body);

// ── Recompute jobs (T4-S12; §7.1) ────────────────────────────────────────────

/** { recompute_jobs | jobs } — filters: state. */
export const listRecomputeJobs = (params?: AssuranceParams): Promise<AssuranceBody> =>
  api.get(`${LX}/recompute-jobs`, { params: query(params) }).then(body);

/**
 * Queue a recompute: { trigger: { object_type, object_id, version }, scope: { asset_keys,
 * route_ids, trip_ids, from, to }, range: { from, to } }. The server pins the config
 * snapshot and stores `estimate: { positions, trips, events }` on the job it returns.
 * Nothing changes until a second user publishes the diff.
 */
export const createRecomputeJob = (data: AssuranceParams): Promise<AssuranceBody> => api.post(`${LX}/recompute-jobs`, data).then(body);

/** The job with its `progress` and headline `diff`. */
export const getRecomputeJob = (id: Id): Promise<AssuranceBody> => api.get(`${LX}/recompute-jobs/${id}`).then(body);

/**
 * recompute_diff_rows page: filters table, change, page. Signed-off trips come back
 * separately (`signed_off` rows, or rows flagged `signed_off: true`).
 */
export const getRecomputeDiff = (id: Id, params?: AssuranceParams): Promise<AssuranceBody> =>
  api.get(`${LX}/recompute-jobs/${id}/diff`, { params: query(params) }).then(body);

/** Publish = the second user's sign-off (lexistar.recompute.signoff); awaiting_signoff → publishing. */
export const signOffRecomputeJob = (id: Id): Promise<AssuranceBody> =>
  api.post(`${LX}/recompute-jobs/${id}:signOff`, {}).then(body);

export const discardRecomputeJob = (id: Id): Promise<AssuranceBody> =>
  api.post(`${LX}/recompute-jobs/${id}:discard`, {}).then(body);

// ── Commissioning: parallel runs and certificates (T4-S15; §7.1) ─────────────

export const listParallelRuns = (params?: AssuranceParams): Promise<AssuranceBody> =>
  api.get(`${LX}/parallel-runs`, { params: query(params) }).then(body);

/** { name, window_from, window_to, asset_keys, lane_ids, floors? } */
export const createParallelRun = (data: AssuranceParams): Promise<AssuranceBody> => api.post(`${LX}/parallel-runs`, clean(data)).then(body);

export const getParallelRun = (id: Id): Promise<AssuranceBody> => api.get(`${LX}/parallel-runs/${id}`).then(body);

/** Template columns for a record kind: { kind, columns: [{ name, required?, description?, example? }] }. */
export const getRecordTemplate = (kind: string): Promise<AssuranceBody> =>
  api.get(`${LX}/parallel-runs/templates/${kind}`).then(body);

/**
 * Import the customer's own records (CSV / XLSX) of one kind as multipart
 * (`file`, `kind`). Body: `{ imported, rejected, errors: [{ row, column?, message }] }`.
 */
export const importParallelRunRecords = (id: Id, kind: string, file: File): Promise<AssuranceBody> => {
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file, file.name || `${kind}.csv`);
  return api
    .post(`${LX}/parallel-runs/${id}/records:import`, form, {
      params: { kind },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    })
    .then(body);
};

/** Idempotent matcher over the run's records. */
export const matchParallelRun = (id: Id): Promise<AssuranceBody> => api.post(`${LX}/parallel-runs/${id}:match`, {}).then(body);

/** The accuracy report (§7.1 report shape). */
export const getParallelRunReport = (id: Id): Promise<AssuranceBody> => api.get(`${LX}/parallel-runs/${id}/report`).then(body);

/** What POST /parallel-runs/:id:signOff takes (ST-00193). */
export interface SignOffParallelRunBody {
  /** Ask for a floor override (202 { pending_override }). */
  override_reason?: string;
  /** Confirm another user's request, naming the one read (pending_override.id). */
  confirm_override_id?: string;
}

/**
 * Sign-off → certificate. Below a floor the override takes two calls by two users
 * (ST-00193): { override_reason } answers 202 { pending_override }; a DIFFERENT user then
 * confirms that request with { confirm_override_id: pending_override.id } (409
 * override_changed when it was replaced since; an empty body never confirms). The second user
 * is never named in the body (the server refuses second_actor with 400).
 */
export const signOffParallelRun = (
  id: Id,
  { override_reason, confirm_override_id }: SignOffParallelRunBody = {}
): Promise<AssuranceBody> =>
  api
    .post(`${LX}/parallel-runs/${id}:signOff`, clean({ override_reason, confirm_override_id }))
    .then(body);

/** { certificate: { id, number, issued_at, issued_by, content, hash, watermark } } */
export const getCertificate = (id: Id): Promise<AssuranceBody> => api.get(`${LX}/certificates/${id}`).then(body);

// ── Realtime (§2.4) ──────────────────────────────────────────────────────────

export const LEXISTAR_REVIEW = 'lexistar:review';
export const LEXISTAR_RECOMPUTE = 'lexistar:recompute';
const LEXISTAR_EVENT = 'lexistar:event';

const socketBaseUrl = () =>
  import.meta.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5002';

const sameTenant = (msg: unknown, tenantId: Id): boolean => {
  const t = isObj(msg) ? msg.tenant_id : undefined;
  return t === undefined || t === null ? true : String(t) === String(tenantId);
};

/** A realtime message (lexistar:review / lexistar:recompute payload or an EventV1). */
export type AssuranceMessage = Obj;
export type AssuranceStatus = 'live' | 'reconnecting' | 'offline';

/**
 * Join /tracking for one tenant and forward the assurance messages.
 *
 * `review: true` also asks for the review room (`join:review` — the relay's join handler
 * for tenant:{id}:review; the room name is in §2.4, the join event is not, so this is the
 * UI's reading). `onReview(msg)` gets each `lexistar:review` payload and, as a fallback,
 * each `lexistar:event` of family ASR.REVIEW_ITEM_*. `onRecompute(msg)` gets
 * `lexistar:recompute` and ASR.RECOMPUTE_* events. Messages for another tenant are
 * dropped. Never joins without a tenant id.
 *
 * @returns {() => void} unsubscribe
 */
export function subscribeAssurance({
  tenantId,
  review = false,
  onReview,
  onRecompute,
  onStatus,
}: {
  tenantId: Id | null | undefined;
  review?: boolean;
  onReview?: (msg: AssuranceMessage) => void;
  onRecompute?: (msg: AssuranceMessage) => void;
  onStatus?: (status: AssuranceStatus) => void;
}): () => void {
  if (tenantId === null || tenantId === undefined || tenantId === '') return () => {};
  const socket = io(`${socketBaseUrl()}/tracking`, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
  });
  const join = () => {
    socket.emit('lexistar:join', { tenantId });
    if (review) socket.emit('join:review', { tenantId });
    onStatus?.('live');
  };
  const reviewHandler = (msg: unknown) => {
    if (sameTenant(msg, tenantId)) onReview?.(isObj(msg) ? msg : {});
  };
  const recomputeHandler = (msg: unknown) => {
    if (sameTenant(msg, tenantId)) onRecompute?.(isObj(msg) ? msg : {});
  };
  const eventHandler = (evt: unknown) => {
    if (!isObj(evt) || !sameTenant(evt, tenantId)) return;
    const type = String(evt.type || '');
    if (type.startsWith('ASR.REVIEW_ITEM_')) onReview?.(evt);
    else if (type.startsWith('ASR.RECOMPUTE_')) onRecompute?.(evt);
  };
  const onDisconnect = () => onStatus?.('reconnecting');
  const onError = () => onStatus?.('offline');

  socket.on('connect', join);
  socket.on('disconnect', onDisconnect);
  socket.on('connect_error', onError);
  socket.on(LEXISTAR_REVIEW, reviewHandler);
  socket.on(LEXISTAR_RECOMPUTE, recomputeHandler);
  socket.on(LEXISTAR_EVENT, eventHandler);

  return () => {
    socket.off('connect', join);
    socket.off('disconnect', onDisconnect);
    socket.off('connect_error', onError);
    socket.off(LEXISTAR_REVIEW, reviewHandler);
    socket.off(LEXISTAR_RECOMPUTE, recomputeHandler);
    socket.off(LEXISTAR_EVENT, eventHandler);
    if (review) socket.emit('leave:review', { tenantId });
    socket.disconnect();
  };
}
